import { cn } from "../../../lib/utils";
import type {
  ApprovalPayload,
  AuditEvent,
  ChangePayload,
  CitationPayload,
  DocumentPayload,
  LockPayload,
  OverridePayload,
  RunPayload,
  SamplePayload,
} from "../../../models/audit/auditEvent";

const hasText = (value: string | null | undefined): value is string =>
  !!value && value.trim().length > 0;

const SCOPE_LABEL: Record<DocumentPayload["scope"] & string, string> = {
  session: "Session",
  sample: "Sample",
  control: "Control",
  supplementary: "Supplementary",
};

/** Uppercase micro-label used for every section header in the detail body,
 * so sections share one visual level distinct from per-item labels. */
function SectionLabel({ children }: { children: string }) {
  return (
    <div className="text-[10px] font-medium uppercase tracking-wider text-font-tertiary">
      {children}
    </div>
  );
}

interface ChangeRow {
  /** Gutter label naming what the row's value belongs to (a sample, a field,
   * a config target). Null renders the redline full-width — used when the
   * label would only repeat scope the pane header already shows. */
  label: string | null;
  before: string;
  after: string;
}

/**
 * The one "Changes made" surface every value-altering event renders through:
 * a definition-list of changes — quiet gutter label (what changed), a single
 * vertical rule, then the old value struck through above the new value
 * highlighted. Whole-value lines, no intra-word diffing: formatted values
 * (dates, amounts) read as replacements, and each side stays legible.
 */
function ChangesMadeSection({ rows }: { rows: ChangeRow[] }) {
  return (
    <div className="space-y-1.5">
      <SectionLabel>Changes made</SectionLabel>
      <div className="space-y-2 pl-4">
        {rows.map((row, index) => (
          <div key={`${row.label ?? ""}-${index}`} className="flex">
            {row.label !== null && (
              <span className="w-28 shrink-0 break-words py-0.5 pr-3 text-[11px] font-medium leading-relaxed text-stone-500">
                {row.label}
              </span>
            )}
            <div
              className={cn(
                "min-w-0 flex-1 space-y-0.5 whitespace-pre-wrap break-words py-0.5 text-xs leading-relaxed",
                row.label !== null && "border-l border-stone-200 pl-3",
              )}
            >
              {hasText(row.before) && (
                <div className="text-red-600 line-through decoration-red-400">
                  {row.before}
                </div>
              )}
              {hasText(row.after) && (
                <div className="rounded-sm bg-green-50 text-green-700">
                  {row.after}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Quoted block used for run instructions and approval comments. */
function QuoteBlock({ label, text }: { label: string; text: string }) {
  return (
    <div className="space-y-1.5">
      <SectionLabel>{label}</SectionLabel>
      <blockquote className="whitespace-pre-wrap break-words rounded-md border-l-2 border-stone-300 bg-stone-50 px-3 py-2 text-xs italic text-stone-600">
        {text}
      </blockquote>
    </div>
  );
}

const hasChangeDetail = (payload: ChangePayload): boolean =>
  (payload.fieldChanges?.length ?? 0) > 0 ||
  hasText(payload.before) ||
  hasText(payload.after);

const hasSampleDetail = (payload: SamplePayload): boolean =>
  hasText(payload.before) || hasText(payload.after);

const differs = (
  before: string | null | undefined,
  after: string | null | undefined,
): boolean => (before ?? "") !== (after ?? "");

const hasOverrideDetail = (payload: OverridePayload): boolean =>
  (payload.fieldChanges?.length ?? 0) > 0 ||
  differs(payload.outcomeBefore, payload.outcomeAfter) ||
  differs(payload.conclusionBefore, payload.conclusionAfter);

// A run's scope lives in the pane header as title subtext (see
// `AuditEventDetailPane`), not in the body — so the body has detail only when
// the run carried instructions or computed cell diffs.
const hasRunDetail = (payload: RunPayload): boolean =>
  hasText(payload.instructions) || payload.cellChanges != null;

/**
 * The run's concrete scope, one middot-joined line for the pane header's
 * subtext. Named where the target set is small (the cell, the sample),
 * counted where it's large (a column's samples), null where it's the whole
 * test — that scope is implied by the event type itself.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function runScopeSummary(
  event: AuditEvent,
  columnNameById?: (columnId: string) => string | undefined,
): string | null {
  if (!event.run) {
    return null;
  }
  const { sampleCount, targetColumnIds } = event.run;
  // Ids resolve to attribute names only for the procedure whose table-shell
  // is cached; unresolved ids are dropped rather than shown raw.
  const columnNames = (targetColumnIds ?? [])
    .map((columnId) => columnNameById?.(columnId))
    .filter(hasText);
  const attributeNames = hasText(event.attributeLabel)
    ? event.attributeLabel
    : columnNames.join(", ");
  const sampleCountLabel =
    typeof sampleCount === "number"
      ? `${sampleCount} ${sampleCount === 1 ? "sample" : "samples"}`
      : null;
  switch (event.eventType) {
    case "test_run":
    case "documents_rematched":
      return null;
    case "sample_rerun":
      return hasText(event.sampleLabel) ? event.sampleLabel : sampleCountLabel;
    case "cell_rerun":
      return (
        [attributeNames, event.sampleLabel].filter(hasText).join(" · ") ||
        sampleCountLabel
      );
    case "column_rerun":
      return [attributeNames, sampleCountLabel].filter(hasText).join(" · ");
    default:
      return [sampleCountLabel, attributeNames].filter(hasText).join(" · ");
  }
}

const hasDocumentDetail = (payload: DocumentPayload): boolean =>
  (payload.documentNames?.length ?? 0) > 0 || hasText(payload.detail);

const hasCitationDetail = (payload: CitationPayload): boolean =>
  hasText(payload.summary) || hasText(payload.documentName);

// Locks belong to the cell, not a person — `lockedByUserId` is provenance
// that normally matches the event's actor, so there is nothing to add. A
// mismatch means the endpoint was driven in a way the product never intends,
// which is exactly what an audit log should surface.
const hasLockDetail = (event: AuditEvent, payload: LockPayload): boolean =>
  hasText(payload.lockedByUserId) &&
  payload.lockedByUserId !== event.triggeredByUserId;

const hasApprovalDetail = (payload: ApprovalPayload): boolean =>
  hasText(payload.comment);

/**
 * Whether `event` carries a payload worth expanding into a detail row.
 * `AuditEventRow` uses this to decide whether to render the expand chevron
 * at all — every field on every payload is optional, so most families need
 * more than a null-check to know if there's anything renderable.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function hasDetail(event: AuditEvent): boolean {
  return (
    (!!event.change && hasChangeDetail(event.change)) ||
    (!!event.sample && hasSampleDetail(event.sample)) ||
    (!!event.override && hasOverrideDetail(event.override)) ||
    (!!event.run && hasRunDetail(event.run)) ||
    (!!event.document && hasDocumentDetail(event.document)) ||
    (!!event.citation && hasCitationDetail(event.citation)) ||
    (!!event.lock && hasLockDetail(event, event.lock)) ||
    (!!event.approval && hasApprovalDetail(event.approval))
  );
}

interface AuditEventDetailProps {
  event: AuditEvent;
  /** Resolves a workspace member id to a display email, used for the
   * `lock` payload's `lockedByUserId`. Falls back to the raw id. */
  memberEmailById?: (userId: string) => string | undefined;
}

/**
 * Expanded detail for one audit event. Exactly one payload field is set per
 * event (selected by `eventType`); this renders whichever one is both
 * present and has renderable content, tolerating every field being null.
 */
export function AuditEventDetail({
  event,
  memberEmailById,
}: AuditEventDetailProps) {
  if (event.change && hasChangeDetail(event.change)) {
    // A multi-field edit (e.g. a control config save) lists one row per
    // changed field; a single-target change renders one row, labeled only
    // when the target adds something beyond the header breadcrumb (e.g. a
    // support-cell edit's column is already named there).
    const { target, fieldChanges } = event.change;
    const rows =
      fieldChanges != null && fieldChanges.length > 0
        ? fieldChanges.map((change) => ({
            label: change.field,
            before: change.before ?? "",
            after: change.after ?? "",
          }))
        : [
            {
              label:
                hasText(target) && target !== event.attributeLabel
                  ? target
                  : null,
              before: event.change.before ?? "",
              after: event.change.after ?? "",
            },
          ];
    return <ChangesMadeSection rows={rows} />;
  }

  if (event.sample && hasSampleDetail(event.sample)) {
    return (
      <ChangesMadeSection
        rows={[
          {
            label: event.sample.fieldName ?? "Value",
            before: event.sample.before ?? "",
            after: event.sample.after ?? "",
          },
        ]}
      />
    );
  }

  if (event.override && hasOverrideDetail(event.override)) {
    const {
      outcomeBefore,
      outcomeAfter,
      conclusionBefore,
      conclusionAfter,
      fieldChanges,
    } = event.override;

    // `fieldChanges` carries every edited field with its own before/after, so
    // it supersedes the outcome/conclusion pair when present; the pair covers
    // events that only moved those two, skipping any section whose before and
    // after are identical.
    const sections =
      fieldChanges && fieldChanges.length > 0
        ? fieldChanges.map((change) => ({
            label: change.field,
            before: change.before ?? "",
            after: change.after ?? "",
          }))
        : [
            {
              label: "Outcome",
              before: outcomeBefore ?? "",
              after: outcomeAfter ?? "",
            },
            {
              label: "Conclusion",
              before: conclusionBefore ?? "",
              after: conclusionAfter ?? "",
            },
          ].filter((section) => differs(section.before, section.after));

    return sections.length === 0 ? null : (
      <ChangesMadeSection rows={sections} />
    );
  }

  if (event.run && hasRunDetail(event.run)) {
    const { instructions, cellChanges } = event.run;
    // Instructions lead, changes follow: what the auditor asked for, then
    // what the rerun did about it.
    return (
      <div className="space-y-3">
        {hasText(instructions) && (
          <QuoteBlock label="Custom instructions" text={instructions} />
        )}
        {cellChanges != null &&
          (cellChanges.length === 0 ? (
            <div className="space-y-1.5">
              <SectionLabel>Changes made</SectionLabel>
              <p className="text-xs italic text-stone-400">
                No cell values changed — the rerun landed on the same values.
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              <ChangesMadeSection
                rows={cellChanges.map((change) => ({
                  // A single row whose label just repeats the header's sample
                  // renders full-width — the sample is already stated once.
                  label:
                    cellChanges.length === 1 &&
                    change.sampleLabel === event.sampleLabel
                      ? null
                      : (change.sampleLabel ?? "Sample"),
                  before: change.before ?? "",
                  after: change.after ?? "",
                }))}
              />
              {(event.run.sampleCount ?? 0) > cellChanges.length && (
                // The diff lists only samples whose value moved; this line
                // closes the arithmetic against the header's sample count.
                <p className="pl-4 text-[11px] italic text-stone-400">
                  {(event.run.sampleCount ?? 0) - cellChanges.length}{" "}
                  {(event.run.sampleCount ?? 0) - cellChanges.length === 1
                    ? "sample"
                    : "samples"}{" "}
                  unchanged
                </p>
              )}
            </div>
          ))}
      </div>
    );
  }

  if (event.document && hasDocumentDetail(event.document)) {
    const { documentNames, scope, detail } = event.document;
    return (
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <SectionLabel>Documents</SectionLabel>
          {scope && (
            <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-medium text-stone-500">
              {SCOPE_LABEL[scope]}
            </span>
          )}
        </div>
        <div className="space-y-0.5 pl-4 text-xs leading-relaxed text-stone-700">
          {(documentNames ?? []).map((name, index) => (
            <div key={`${name}-${index}`}>{name}</div>
          ))}
          {hasText(detail) && <div className="text-stone-500">{detail}</div>}
        </div>
      </div>
    );
  }

  if (event.citation && hasCitationDetail(event.citation)) {
    const { summary, isKey, documentName, location } = event.citation;
    // The cited document anchors the gutter, with the step the citation hangs
    // off ("Reperformance Step 2") stacked quietly beneath it; the auditor's
    // summary sits right of the rule, mirroring the changes-made grammar.
    // Older rows missing halves collapse to a single full-width line.
    const docLine = hasText(documentName) ? documentName : null;
    const keyChip = isKey ? (
      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
        Key
      </span>
    ) : null;
    return (
      <div className="space-y-1.5">
        <SectionLabel>Citation</SectionLabel>
        {docLine && hasText(summary) ? (
          <div className="flex pl-4">
            <span className="w-36 shrink-0 break-words py-0.5 pr-3 text-[11px] leading-relaxed">
              <span className="font-medium text-stone-500">{docLine}</span>
              {hasText(location) && (
                <span className="mt-0.5 block text-[10px] text-stone-400">
                  {location}
                </span>
              )}
            </span>
            <div className="min-w-0 flex-1 border-l border-stone-200 py-0.5 pl-3 text-xs leading-relaxed text-stone-700">
              <span className="mr-1.5">{summary}</span>
              {keyChip}
            </div>
          </div>
        ) : (
          <div className="space-y-0.5 pl-4 text-xs leading-relaxed text-stone-700">
            <div className="flex items-center gap-2">
              <span className="min-w-0">{docLine ?? summary}</span>
              {keyChip}
            </div>
            {hasText(location) && (
              <div className="text-[11px] text-stone-400">{location}</div>
            )}
          </div>
        )}
      </div>
    );
  }

  if (
    event.lock &&
    hasLockDetail(event, event.lock) &&
    hasText(event.lock.lockedByUserId)
  ) {
    const lockedBy = event.lock.lockedByUserId;
    return (
      <div className="text-xs text-stone-600">
        Lock held by {memberEmailById?.(lockedBy) ?? lockedBy}
      </div>
    );
  }

  if (
    event.approval &&
    hasApprovalDetail(event.approval) &&
    hasText(event.approval.comment)
  ) {
    return <QuoteBlock label="Comment" text={event.approval.comment} />;
  }

  return null;
}
